import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReportModal from "../../../components/ReportModal/ReportModal";
import { bloquearUsuario } from "../../../utils/bloqueos";
import {
  Avatar,
  Button,
  Card,
  Carousel,
  Col,
  Flex,
  List,
  Row,
  Rate,
  Typography,
  InputNumber,
  Select,
  DatePicker,
  Skeleton,
  Space,
  Divider,
  Tag,
  Alert,
  Result,
} from "antd";
import { db } from "../../../firebaseConfig";
import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useUser } from "../../../context/UserContext";
import { Toaster, toast } from "sonner";
import {
  HeartOutlined,
  HeartFilled,
  ShoppingCartOutlined,
  DeleteOutlined,
  DownOutlined,
  UpOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  LockOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import visaMaster from "../../../assets/visa-mastercard.png";
import openpayLogo from "../../../assets/openpay.png";
import {
  addToCartServicioDetalle,
  eliminarServicioCarrito,
} from "../../../utils/servicios/serviciosUtils";
import { handleFavoriteServicioAction } from "../../../utils/favoritos/favoritosUtils";
import locale from "antd/es/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es"; 
import "./Servicio.scss"; 
import { MODERACION_ACTIVA } from "../../../config";

dayjs.locale("es");
const { Title, Paragraph, Text } = Typography;

export default function Servicio() {
  const { id } = useParams();
  const [servicio, setServicio] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estados de lógica de negocio
  const [cantidad, setCantidad] = useState(1);
  const [horaInicioSeleccionada, setHoraInicioSeleccionada] = useState(null);
  const [diaSeleccionado, setDiaSeleccionado] = useState(undefined);
  const [cantidadDuracion, setCantidadDuracion] = useState(1);
  const [enCarrito, setEncarrito] = useState(false);
  const [horasLibres, setHorasLibres] = useState([]);
  const [cargandoHoras, setCargandoHoras] = useState(false);
  const [motivoHoras, setMotivoHoras] = useState(null);
  
  // Estados de UI y Reseñas
  const [reseñas, setReseñas] = useState([]);
  const [reporte, setReporte] = useState(null);
  const [calificacionPromedio, setCalificacionPromedio] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [tienda, setTienda] = useState(null);
  const [expandedDesc, setExpandedDesc] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [stickyVisible, setStickyVisible] = useState(false);
  const buyButtonRef = useRef(null);
  const carouselRef = useRef(null);

  const { userData, userFavorites, cartItems } = useUser();
  const navigate = useNavigate();

  // --- CARGA INICIAL ---
  useEffect(() => {
    const fetchServiceData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "servicios", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const serviceData = { id: docSnap.id, ...docSnap.data() };
          setServicio(serviceData);
          
          if(serviceData.vendedorUid){
             const tiendaRef = doc(db, "tiendas", serviceData.vendedorUid);
             const tiendaSnap = await getDoc(tiendaRef);
             if(tiendaSnap.exists()) setTienda({ id: tiendaSnap.id, ...tiendaSnap.data() });
          }
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    };

    fetchServiceData();
    obtenerReseñas();
  }, [id]);

  // Sincronizar Carousel cuando cambia activeImg
  useEffect(() => {
    carouselRef.current?.goTo(activeImg);
  }, [activeImg]);

  // --- FAVORITOS Y UI ---
  useEffect(() => {
    if (userFavorites?.servicios?.length > 0) {
      setIsFavorite(userFavorites.servicios.includes(id));
    }
  }, [id, userFavorites, userData]);

  // --- CARRITO SYNC ---
  useEffect(() => {
    if (servicio) {
      const itemEnCarrito = cartItems?.servicios?.find((item) => item.id === servicio.id);
      if (itemEnCarrito) {
        setEncarrito(true);
        setHoraInicioSeleccionada(itemEnCarrito.horaInicioSeleccionada);
        setDiaSeleccionado(itemEnCarrito.diaSeleccionado);
        setCantidad(itemEnCarrito.cantidad || 1);
        setCantidadDuracion(itemEnCarrito.cantidadDuracion || 1);
      } else {
        setEncarrito(false);
      }
    }
  }, [cartItems, servicio]);

  // Generar horarios de fallback basados en el horario de atención del servicio
  const generarHorariosFallback = (servicioData) => {
    if (!servicioData?.horaInicio || !servicioData?.horaFin) return [];
    const parseH = (hStr) => {
      const parts = String(hStr || "").split(":");
      if (parts.length < 2) return null;
      const h = Number(parts[0]);
      const m = Number(parts[1]);
      return isNaN(h) || isNaN(m) ? null : h * 60 + m;
    };
    const iniMin = parseH(servicioData.horaInicio);
    const finMin = parseH(servicioData.horaFin);
    if (iniMin === null || finMin === null) return [];

    const slots = [];
    const step = 60;
    for (let m = Math.ceil(iniMin / 60) * 60; m <= finMin; m += step) {
      const hStr = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
      slots.push(hStr);
    }
    return slots;
  };

  // --- DISPONIBILIDAD REAL (reservas de otros clientes, horario y días del servicio) ---
  useEffect(() => {
    if (!servicio || !diaSeleccionado) {
      setHorasLibres([]);
      setMotivoHoras(null);
      return;
    }
    let cancelado = false;
    setCargandoHoras(true);
    httpsCallable(getFunctions(), "disponibilidadServicio")({
      servicioId: servicio.id,
      dia: diaSeleccionado,
      cantidadDuracion,
      unidades: cantidad,
    })
      .then(({ data }) => {
        if (cancelado) return;
        setHorasLibres(data.horas || []);
        setMotivoHoras(data.motivo || null);
        setHoraInicioSeleccionada((actual) => (actual && (data.horas || []).includes(actual) ? actual : null));
      })
      .catch((error) => {
        if (cancelado) return;
        console.warn("Disponibilidad remota no disponible, usando horario local del servicio:", error?.message);
        const fallback = generarHorariosFallback(servicio);
        setHorasLibres(fallback);
        setMotivoHoras(fallback.length ? null : "No hay horarios en el rango de atención.");
        setHoraInicioSeleccionada((actual) => (actual && fallback.includes(actual) ? actual : null));
      })
      .finally(() => !cancelado && setCargandoHoras(false));
    return () => {
      cancelado = true;
    };
  }, [servicio, diaSeleccionado, cantidadDuracion, cantidad]);

  // --- HANDLERS ---
  function disabledDate(current) {
    const dia = dayjs(current).locale("es").format("dddd").toLowerCase();
    return !servicio?.diasDisponibles?.includes(dia) || dayjs(current).isBefore(dayjs().startOf("day"), "day");
  }

  // Lógica de Scroll para Barra Sticky
  useEffect(() => {
    const handleScroll = () => {
      if (buyButtonRef.current) {
        const rect = buyButtonRef.current.getBoundingClientRect();
        setStickyVisible(rect.bottom < 0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleToggleFavorite = () => {
    if (!buttonDisabled) {
      if (!userData) {
        toast.info("Inicia sesión para agregar a favoritos.");
        return;
      }
      // No disponible: solo se permite quitarlo de favoritos, no agregarlo
      if (noSePuedeReservar && !isFavorite) {
        toast.error(`${motivoNoDisponible} No se puede agregar a favoritos.`);
        return;
      }
      const action = isFavorite ? "remove" : "add";
      setIsFavorite(!isFavorite);
      setButtonDisabled(true);
      handleFavoriteServicioAction(id, action, userData);
      setTimeout(() => setButtonDisabled(false), 1000);
    }
  };

  const obtenerReseñas = async () => {
    const q = query(collection(db, "resenas"), where("idservicio", "==", id));
    const snap = await getDocs(q);
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setReseñas(data);
    const prom = data.reduce((acc, curr) => acc + curr.calificacion, 0) / data.length || 0;
    setCalificacionPromedio(prom);
  };

  const calcularFechaFin = () => {
    if (!diaSeleccionado) return null;
    if (servicio?.duracion === "dia") {
      return dayjs(diaSeleccionado).add(cantidadDuracion, "day");
    }
    return dayjs(diaSeleccionado);
  };

  const calcularHoraFin = () => {
    if (!horaInicioSeleccionada) return null;
    if (servicio?.duracion === "hora") {
      const parts = horaInicioSeleccionada.split(":").map(Number);
      const totalMin = parts[0] * 60 + parts[1] + (Number(cantidadDuracion) || 1) * 60;
      const endH = Math.floor(totalMin / 60) % 24;
      const endM = totalMin % 60;
      return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
    }
    return horaInicioSeleccionada;
  };

  const calcularTotalEstimado = () => {
    const precio = Number(servicio?.total) || 0;
    const dur = servicio?.duracion === "unico" ? 1 : (Number(cantidadDuracion) || 1);
    const cant = servicio?.stock > 0 ? (Number(cantidad) || 1) : 1;
    return (precio * dur * cant).toFixed(2);
  };

  // Un servicio pausado o de una tienda bloqueada no se puede reservar
  const motivoNoDisponible = !servicio
    ? null
    : servicio.pausa || servicio.deshabilitado || servicio.eliminado
    ? "Este servicio ya no está disponible."
    : tienda && (tienda.activado === false || tienda.pausa || tienda.blacklist)
    ? "La tienda de este servicio no está activa en este momento."
    : null;
  const noSePuedeReservar = !!motivoNoDisponible;

  const handleAddToCart = () => {
      if(noSePuedeReservar) return toast.error(motivoNoDisponible);
      if(!userData) {
        toast.info("Inicia sesión para reservar este servicio.");
        return navigate("/login");
      }
      if(!diaSeleccionado || !horaInicioSeleccionada) return toast.error("Elige la fecha y el horario de tu reserva.");
      addToCartServicioDetalle(servicio, userData, cantidad, cantidadDuracion, diaSeleccionado, horaInicioSeleccionada);
  };

  if (loading) return (
    <div className="service-detail-container">
       <Skeleton active paragraph={{ rows: 10 }} />
    </div>
  );

  if (!servicio) return (
    <div className="service-detail-container">
      <Result
        status="404"
        title="Este servicio ya no existe"
        subTitle="Pudo haber sido eliminado por el vendedor."
        extra={<Button type="primary" onClick={() => navigate("/")}>Explorar servicios</Button>}
      />
    </div>
  );

  return (
    <div className="service-detail-container">
      {/* BARRA STICKY SUPERIOR */}
      <div className={`sticky-buy-bar ${stickyVisible ? "visible" : ""}`}>
        <div className="sticky-container">
          <div className="product-compact-info">
            <img src={servicio?.imagen?.[0]?.url} alt={servicio?.nombre} />
            <div className="text-info">
              <Text strong>{servicio?.nombre}</Text>
              <Text type="secondary" size="small">
                Servicio Profesional
              </Text>
            </div>
          </div>
          <div className="sticky-actions">
            <Text className="price-compact">${servicio?.total} MXN</Text>
            <Button
              type="primary"
              className="sticky-add-btn"
              disabled={noSePuedeReservar}
              onClick={handleAddToCart}
            >
              Reservar
            </Button>
          </div>
        </div>
      </div>

      <Toaster richColors position="top-center" />

      <Row gutter={[32, 0]} className="main-service-row">
        <button className="back-link" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Volver
        </button>
        {/* COLUMNA IZQUIERDA: IMÁGENES CON MINIATURAS */}
        <Col xs={24} md={12} lg={14}>
          <div className="gallery-layout">
            <div className="thumbnails-container">
              {servicio?.imagen?.map((img, i) => (
                <div
                  key={i}
                  className={`thumbnail-item ${activeImg === i ? "active" : ""}`}
                  onMouseEnter={() => setActiveImg(i)}
                  onClick={() => setActiveImg(i)}
                >
                  <img
                    src={img.url}
                    alt={`Miniatura ${i}`}
                    onError={(e) => {
                      e.target.style.opacity = "0.3";
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="main-carousel-container">
              <div className="carousel-wrapper">
                <Carousel
                  autoplay={false}
                  dots={false}
                  ref={carouselRef}
                  afterChange={(current) => setActiveImg(current)}
                >
                  {servicio?.imagen?.map((img, i) => (
                    <div key={i} className="img-slide">
                      <img
                        src={img.url}
                        alt={servicio.nombre}
                        onError={(e) => {
                          e.target.style.opacity = "0.3";
                        }}
                      />
                    </div>
                  ))}
                </Carousel>
                <Button
                  className="fav-btn-float"
                  shape="circle"
                  icon={
                    isFavorite ? (
                      <HeartFilled style={{ color: "#ff4d4f" }} />
                    ) : (
                      <HeartOutlined />
                    )
                  }
                  onClick={handleToggleFavorite}
                />
              </div>
            </div>
          </div>
        </Col>

        {/* COLUMNA DERECHA: INFO Y CONTROLES */}
        <Col xs={24} md={12} lg={10}>
          <div className="service-info-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              
              <Title level={2} className="service-title">{servicio?.nombre}</Title>

              <Flex align="center" gap={8} className="rating-container">
                <Rate disabled allowHalf value={calificacionPromedio} style={{ fontSize: 16 }} />
                <Text type="secondary">({reseñas.length} reseñas)</Text>
              </Flex>
              {userData?.uid && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => setReporte({ tipo: "servicio", targetId: id, targetUid: servicio?.vendedorUid, contexto: servicio?.nombre })}
                >
                  Reportar este servicio
                </Button>
              )}

               <div className="price-tag">
                <Text className="currency">$</Text>
                <Text className="amount">{servicio?.total}</Text>
                <Text className="currency">MXN</Text>
              </div>

              {/* BADGES DE BENEFICIO */}
              <div style={{ marginTop: 10 }}>
                <div className="urgency-badge">
                  <ClockCircleOutlined /> Reserva Inmediata
                </div>
                <div className="urgency-badge" style={{ marginLeft: 8 }}>
                  <CheckCircleOutlined /> Calidad Blizu
                </div>
              </div>

              <div className={`description-wrapper ${expandedDesc ? "expanded" : ""}`} onClick={() => setExpandedDesc(!expandedDesc)}>
                <Paragraph className="description-text">{servicio?.descripcion}</Paragraph>
                <div className="read-more-trigger">
                  {expandedDesc ? <span>Ocultar <UpOutlined /></span> : <span>Ver más <DownOutlined /></span>}
                </div>
              </div>

              <Card size="small" style={{width: '100%', marginBottom: 10}}>
                 <Space split={<Divider type="vertical" />}>
                    <Text><ClockCircleOutlined /> {servicio?.horaInicio} - {servicio?.horaFin}</Text>
                    {servicio?.stock && <Text>Disponibles: {servicio.stock}</Text>}
                  </Space>
                </Card>

                {/* CONTROLES DE RESERVA */}
                <div className="service-controls" ref={buyButtonRef}>
                  <div className="controls-grid">
                    <div className="control-field">
                      <span className="control-label">
                        <ClockCircleOutlined /> {servicio?.tipoServicio === "renta" ? "Fecha de entrega" : "Fecha de inicio"}
                      </span>
                      <DatePicker
                        style={{ width: "100%" }}
                        disabledDate={disabledDate}
                        value={diaSeleccionado ? dayjs(diaSeleccionado, "YYYY-MM-DD") : null}
                        onChange={(_, str) => setDiaSeleccionado(str || undefined)}
                        placeholder="Selecciona fecha"
                        locale={locale}
                        inputReadOnly
                      />
                    </div>

                    <div className="control-field">
                      <span className="control-label">
                        <ClockCircleOutlined /> {servicio?.tipoServicio === "renta" ? "Hora de entrega" : "Hora de inicio"}
                      </span>
                      <Select
                        style={{ width: "100%" }}
                        placeholder={diaSeleccionado ? "Selecciona horario" : "Primero elige fecha"}
                        disabled={!diaSeleccionado}
                        loading={cargandoHoras}
                        value={horaInicioSeleccionada || undefined}
                        onChange={setHoraInicioSeleccionada}
                        options={horasLibres.map((h) => ({ value: h, label: h }))}
                        notFoundContent={motivoHoras || "Sin horarios disponibles"}
                      />
                    </div>
                  </div>

                  {diaSeleccionado && !cargandoHoras && horasLibres.length === 0 && motivoHoras && (
                    <Text type="warning" style={{ fontSize: 12, marginTop: 4, display: "block" }}>{motivoHoras}</Text>
                  )}

                  <div className="controls-grid" style={{ marginTop: 12 }}>
                    {servicio?.duracion === "dia" && (
                      <div className="control-field">
                        <span className="control-label">Días de renta</span>
                        <InputNumber
                          min={1}
                          max={30}
                          style={{ width: "100%" }}
                          value={cantidadDuracion}
                          onChange={(v) => setCantidadDuracion(v || 1)}
                          addonAfter="días"
                        />
                      </div>
                    )}

                    {servicio?.duracion === "hora" && (
                      <div className="control-field">
                        <span className="control-label">Horas de servicio</span>
                        <InputNumber
                          min={1}
                          max={12}
                          style={{ width: "100%" }}
                          value={cantidadDuracion}
                          onChange={(v) => setCantidadDuracion(v || 1)}
                          addonAfter="hrs"
                        />
                      </div>
                    )}

                    {servicio?.stock > 0 && (
                      <div className="control-field">
                        <span className="control-label">
                          Cantidad <Text type="secondary" style={{ fontSize: 11 }}>(Max: {servicio.stock})</Text>
                        </span>
                        <InputNumber
                          min={1}
                          max={servicio.stock}
                          style={{ width: "100%" }}
                          value={cantidad}
                          onChange={(v) => setCantidad(v || 1)}
                        />
                      </div>
                    )}
                  </div>

                  {/* TARJETA DE ITINERARIO LOGÍSTICO */}
                  {diaSeleccionado ? (
                    <div className="reservation-itinerary-card">
                      <div className="itinerary-header">
                        <Text strong style={{ fontSize: 13, color: "#1e293b" }}>Itinerario de tu reserva</Text>
                        <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                          {servicio?.duracion === "dia" ? `${cantidadDuracion} ${cantidadDuracion === 1 ? "día" : "días"} de renta` : `${cantidadDuracion} hrs`}
                        </Tag>
                      </div>

                      <div className="itinerary-timeline">
                        <div className="timeline-point">
                          <span className="point-badge badge-start">🚚</span>
                          <div className="point-details">
                            <span className="point-title">{servicio?.tipoServicio === "renta" ? "Entrega programada" : "Inicio del servicio"}</span>
                            <span className="point-time">
                              {dayjs(diaSeleccionado).format("dddd D [de] MMMM")} · {horaInicioSeleccionada ? `${horaInicioSeleccionada} hrs` : "Hora por elegir"}
                            </span>
                          </div>
                        </div>

                        {servicio?.duracion === "dia" && (
                          <div className="timeline-point">
                            <span className="point-badge badge-end">🔄</span>
                            <div className="point-details">
                              <span className="point-title">{servicio?.tipoServicio === "renta" ? "Recolección estimada" : "Fin del servicio"}</span>
                              <span className="point-time">
                                {calcularFechaFin()?.format("dddd D [de] MMMM")} · {horaInicioSeleccionada ? `${horaInicioSeleccionada} hrs` : "—"}
                              </span>
                            </div>
                          </div>
                        )}

                        {servicio?.duracion === "hora" && horaInicioSeleccionada && (
                          <div className="timeline-point">
                            <span className="point-badge badge-end">🏁</span>
                            <div className="point-details">
                              <span className="point-title">Fin del servicio</span>
                              <span className="point-time">
                                {dayjs(diaSeleccionado).format("dddd D [de] MMMM")} · {calcularHoraFin()} hrs
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="itinerary-footer">
                        <span className="total-label">
                          Total estimado {servicio?.stock > 0 ? `(${cantidad} un. × ${cantidadDuracion} ${servicio?.duracion === "dia" ? (cantidadDuracion === 1 ? 'día' : 'días') : 'hrs'})` : `(${cantidadDuracion} ${servicio?.duracion === "dia" ? (cantidadDuracion === 1 ? 'día' : 'días') : 'hrs'})`}:
                        </span>
                        <span className="total-amount">${calcularTotalEstimado()} MXN</span>
                      </div>
                    </div>
                  ) : (
                    <div className="reservation-hint-box">
                      <ClockCircleOutlined style={{ color: "#1dbf73", fontSize: 16 }} />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Elige la fecha y hora de entrega para calcular el itinerario y total.
                      </Text>
                    </div>
                  )}
                </div>

              {/* SECCIÓN DE CONFIANZA Y PAGOS */}
              <div className="trust-signals-container">
                <div className="trust-item">
                  <div className="icon-box">
                    <LockOutlined />
                  </div>
                  <Text>Reserva segura</Text>
                </div>
                <div className="trust-item">
                  <div className="icon-box">
                    <CheckCircleOutlined />
                  </div>
                  <Text>Satisfacción garantizada</Text>
                </div>
                <div className="payment-logos">
                  <img src={visaMaster} alt="Visa y Mastercard" />
                  <img src={openpayLogo} alt="Openpay" />
                </div>
              </div>

              {noSePuedeReservar && (
                <Alert
                  type="warning"
                  showIcon
                  message={motivoNoDisponible}
                  description="Por ahora no puedes reservarlo. Puedes quitarlo de favoritos o buscar uno parecido."
                  style={{ marginBottom: 16, borderRadius: 12 }}
                />
              )}

              {/* BOTONES */}
              {enCarrito ? (
                <div style={{display: 'flex', gap: 10, width: '100%'}}>
                    <Button 
                        block 
                        type="primary" 
                        className="add-to-cart-btn"
                        style={{background: '#f3ff10', color: 'black'}}
                        disabled={noSePuedeReservar}
                        onClick={handleAddToCart}
                    >
                        Actualizar Carrito
                    </Button>
                    <Button 
                        block 
                        danger 
                        className="add-to-cart-btn"
                        style={{background: 'white', border: '1px solid red'}}
                        icon={<DeleteOutlined />}
                        onClick={() => eliminarServicioCarrito(userData, id)}
                    >
                    </Button>
                </div>
              ) : (
                <Button
                    type="primary"
                    size="large"
                    block
                    icon={<ShoppingCartOutlined />}
                    className="add-to-cart-btn"
                    disabled={noSePuedeReservar}
                    onClick={handleAddToCart}
                >
                    {noSePuedeReservar ? "No disponible" : "Reservar Servicio"}
                </Button>
              )}

              {tienda && (
                <Link to={`/reseñas/${tienda.id}`}>
                  <Card size="small" hoverable style={{ marginTop: 20, borderRadius: 12 }}>
                    <Flex align="center" gap={12}>
                      <Avatar src={tienda.photo} size="large" />
                      <div>
                        <Text strong style={{ display: "block" }}>Ofrecido por: {tienda.nombre}</Text>
                        <Rate disabled value={tienda.calificacion} style={{ fontSize: 12 }} />
                      </div>
                    </Flex>
                  </Card>
                </Link>
              )}
            </Space>
          </div>
        </Col>
      </Row>

      {/* SECCIÓN RESEÑAS - DETALLADA 🔥 */}
      <div className="reviews-section">
        <Row gutter={[48, 32]}>
          <Col xs={24} lg={8}>
            <div className="reviews-summary-card">
              <Title level={3}>Opiniones del servicio</Title>
              <Flex align="center" gap={16} className="average-container">
                <h2 className="average-number">
                  {calificacionPromedio.toFixed(1)}
                </h2>
                <div className="average-info">
                  <Rate disabled allowHalf value={calificacionPromedio} />
                  <Text type="secondary" style={{ display: "block" }}>
                    {reseñas.length} calificaciones
                  </Text>
                </div>
              </Flex>

              <div className="stars-distribution">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = reseñas.filter(
                    (r) => Math.round(r.calificacion) === star
                  ).length;
                  const percentage =
                    reseñas.length > 0 ? (count / reseñas.length) * 100 : 0;
                  return (
                    <Flex
                      key={star}
                      align="center"
                      gap={8}
                      className="star-row"
                    >
                      <Text style={{ minWidth: "60px" }}>{star} estrellas</Text>
                      <div className="progress-bar-bg">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <Text type="secondary" style={{ minWidth: "30px" }}>
                        {count}
                      </Text>
                    </Flex>
                  );
                })}
              </div>

              <Divider />

            </div>
          </Col>

          <Col xs={24} lg={16}>
            <List
              itemLayout="vertical"
              dataSource={reseñas.filter((r) => !(userData?.bloqueados || []).includes(r.idUser))}
              pagination={{ pageSize: 5 }}
              locale={{
                emptyText: "Aún no hay reseñas. ¡Sé el primero en calificar!",
              }}
              renderItem={(item) => (
                <div className="review-card-pro">
                  <Flex gap={12} align="start">
                    <Avatar
                      src={item.photo}
                      size={48}
                      className="review-avatar"
                    />
                    <div style={{ flex: 1 }}>
                      <Flex justify="space-between" align="center">
                        <Text strong className="review-author">
                          {item.nombre.split("@")[0]}
                        </Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Reseña verificada
                        </Text>
                      </Flex>
                      <Rate
                        disabled
                        defaultValue={item.calificacion}
                        style={{ fontSize: 12, display: "block" }}
                      />
                      <Paragraph
                        className="review-text"
                        style={{ marginTop: 8 }}
                      >
                        {item.descripcion}
                      </Paragraph>
                      {MODERACION_ACTIVA && userData?.uid && item.idUser !== userData.uid && (
                        <Flex gap={12}>
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            onClick={() => setReporte({ tipo: "resena", targetId: item.id, targetUid: item.idUser, contexto: item.descripcion })}
                          >
                            Reportar
                          </Button>
                          <Button
                            type="link"
                            size="small"
                            danger
                            style={{ padding: 0 }}
                            onClick={() => bloquearUsuario(userData.uid, item.idUser).then(() => toast.success("Usuario bloqueado"))}
                          >
                            Bloquear usuario
                          </Button>
                        </Flex>
                      )}
                    </div>
                  </Flex>
                </div>
              )}
            />
          </Col>
        </Row>
      </div>

      {/* MODAL CALIFICAR */}
      {MODERACION_ACTIVA && (
        <ReportModal
          open={!!reporte}
          onClose={() => setReporte(null)}
          tipo={reporte?.tipo}
          targetId={reporte?.targetId}
          targetUid={reporte?.targetUid}
          contexto={reporte?.contexto}
        />
      )}
    </div>
  );
}